<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'registers',
    path: '/registers',
    component: 'RegisterView',
    title: () => pgettext('Register', 'Registers'),
  });
  addRoute({
    name: 'letterRegister',
    path: '/letter-register',
    component: 'RegisterView',
    title: () => pgettext('Register', 'Registers'),
  });
  addRoute({
    name: 'invoiceRegister',
    path: '/invoice-register',
    component: 'RegisterView',
    title: () => pgettext('Register', 'Registers'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RegisterView',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    return {
      sort_mode: 'desc',
      register_id: selectedRegisterId.value,
      version_id: selectedVersionId.value,
    };
  };
  const invoiceTypeBadgeClass = (...args) => {
    const [row] = args;
    const cc = $config.value.invoice.types.colorCodes[row.invoice_type];
    return `bg-${cc.bg} text-${cc.text}`;
  };
  const priorityBadgeClass = (...args) => {
    const [row] = args;
    const priority = row.priority;

    if (priority == 1) {
      return `border border-purple-500 text-purple-500 py-0`;
    }

    if (priority == 2) {
      return `border border-red-500 text-red-500 py-0`;
    }
  };

  const states = ref({
    selectedRegisterId: null,
    selectedVersionId: null,
  });
  const selectedItems = ref([]);
  const statuses = ref(null);
  const selectedStatus = ref(null);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'entry_number',
      name: $fn.value.pgettext('Register', 'No.'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'link',
              href: '#',
            },
            {
              name: 'cellclass',
              value: (row) => ({
                'fw-bold': true,
                'bg-yellow-200': !row.locked,
                'bg-gray-dark-100': row.locked,
                'text-decoration-line-through': row.void,
              }),
            },
          ],
        },
      ],
    },
    {
      field: 'entry_date',
      name: $fn.value.pgettext('Register', 'Date'),
      type: 'date',
    },
    {
      field: 'direction',
      name: $fn.value.pgettext('Register', 'Direction'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'RegisterEntryDirection',
            conversion: 'StartCase',
            filterInput: true,
          },
        ],
      },
    },
    {
      field: 'document_type',
      name: $fn.value.pgettext('Register', 'Document type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'RegisterDocumentType',
            conversion: 'StartCase',
            filterInput: true,
          },
        ],
      },
    },
    {
      field: 'attachments',
      name: $fn.value.pgettext('Register', 'Attachments'),
      type: 'command',
      formatter: {
        name: 'attachment',
      },
    },
    {
      field: 'delivery_modes',
      name: $fn.value.pgettext('Register', 'Delivery mode'),
      formatter: {
        name: 'join',
        separator: ', ',
      },
    },
    {
      field: 'clerk',
      name: $fn.value.pgettext('Register', 'Clerk'),
    },
    {
      field: 'project',
      name: $fn.value.pgettext('Register', 'Project'),
      formatter: {
        name: 'chain',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'code,name',
              source: 'value',
              separator: ' • ',
            },
          },
          {
            name: 'routeLink',
            to: 'project',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'priority',
      name: $fn.value.pgettext('Register', 'Priority'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'mapvalue',
            mapping: {
              0: $fn.value.pgettext('Priority', 'normal'),
              1: $fn.value.pgettext('Priority', 'important'),
              2: $fn.value.pgettext('Priority', 'urgent'),
            },
          },
          {
            name: 'badge',
            pill: true,
            badgeClass: priorityBadgeClass,
          },
        ],
      },
    },
    {
      field: 'document',
      name: $fn.value.pgettext('Register', 'Document'),
      type: 'command',
      formatter: 'registerDocument',
    },
    {
      field: 'partner',
      name: $fn.value.pgettext('Register', 'Partner'),
      formatter: {
        name: 'chain',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'subject',
      name: $fn.value.pgettext('Register', 'Subject'),
    },
    {
      field: 'ref_entry_number',
      name: $fn.value.pgettext('Register', 'Ref.'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'link',
              href: '#',
            },
          ],
        },
      ],
    },
    {
      field: 'status',
      name: $fn.value.pgettext('Register', 'Status'),
    },
    {
      field: 'categories',
      name: $fn.value.pgettext('Register', 'Category'),
    },
    {
      field: 'delivery_date',
      name: $fn.value.pgettext('Register', 'Delivery date'),
      type: 'date',
    },
    {
      field: 'notes',
      name: $fn.value.pgettext('Register', 'Notes'),
    },
  ]);

  const $data = computed(() => ({
    states: states.value,
    selectedItems: selectedItems.value,
    statuses: statuses.value,
    selectedStatus: selectedStatus.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_statuses = () => {
    return $fn.value
      .fetch('get_tags', { parameters: { tag_type: 'register-entry:status' } })
      .then((data) => _.map(data, (p) => p.name));
  };
  const $$load_ds = () => {
    return $fn.value.gridDataSource('list_register_entries', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'states', cache: { type: 'local', key: 'RegisterView.states' } },
      { name: 'selectedItems' },
      { name: 'statuses' },
      { name: 'selectedStatus' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { statuses: $$load_statuses, ds: $$load_ds },
    ux: {},
    dataRefs: {
      states,
      selectedItems,
      statuses,
      selectedStatus,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedRegisterId = computed(() => {
    if (loaded.value) {
      return states.value.selectedRegisterId;
    }
    return null;
  });
  const selectedVersionId = computed(() => {
    if (loaded.value) {
      return states.value.selectedVersionId;
    }
    return null;
  });
  const isRegisterSelected = computed(() => {
    return selectedRegisterId.value && selectedVersionId.value;
  });
  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('Register', 'Registers'),
      icon: $config.value.register.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_7bg4buya8_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_reloadGrid_qdul43ta6_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2248226175642056_reloadGrid_rnw99lg8q_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const inst_evh_2248226175642056_close_ta9ybs1rj_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2248226175642056_showModal_x4uqpowz3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_dybgri7gx_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'entry_number';
  };
  const act_2248226175642056_reloadGrid_c9q1bjjgf_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2248226175642056_reloadGrid_soo2dfu3c_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const inst_evh_2248226175642056_close_x122nr1n0_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2248226175642056_showModal_819f2nr4y_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        oid: $event.data.data.ref_entry_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_qzhk6nqph_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'ref_entry_number';
  };
  const act_2248226175642056_script_yzss1ku3r_value_callable = async (
    $event
  ) => {
    return $app.value.attachments.downloadAttachmentFromGridCommand($event);
  };
  const act_2248226175642056_script_yzss1ku3r_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_yzss1ku3r_value_callable($event);
      },
    };
  };
  const inst_evh_2248226175642056_command_z0i6334cq_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'attachments';
  };
  const act_2248226175642056_reloadGrid_op5ajbcdf_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2248226175642056_reloadGrid_cqgnt76uk_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const inst_evh_2248226175642056_close_naukgmo34_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2248226175642056_showModal_wxkxmt1re_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.document.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_8yewxjl6g_getWhen = async (
    $event
  ) => {
    return (
      $event.data.column.id === 'document' &&
      !!$event.data.data.document &&
      !!$event.data.data.document.invoice_number
    );
  };
  const act_2248226175642056_setData_11wtww4p1_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_setData_kvol6xsr9_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'states',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_reloadGrid_1ii9ar4yh_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2095089031469157_reloadGrid_xwnt9qv7e_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2095089031469157_reloadGrid_jqy5gz3h5_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const inst_evh_2095089031469157_close_7eh24imf7_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2095089031469157_showModal_v6myx8adi_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        registerId: states.value.selectedRegisterId,
        versionId: states.value.selectedVersionId,
      },
    };
  };
  const act_7439928539205587_setData_qjyqhmw51_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedStatus',
      value: $event.scope.status,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7439928539205587_reloadGrid_olydkpb4o_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_7439928539205587_request_at5ivkzvh_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_register_entry',
      data: _.map(selectedItems.value, (p) => ({
        id: p.id,
        status: selectedStatus.value,
      })),
    };
  };
  const act_2155315188409751_setData_yux6pg2ng_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedStatus',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2155315188409751_reloadGrid_5r0d5rhui_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2155315188409751_request_egzkzvqij_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_register_entry',
      data: _.map(selectedItems.value, (p) => ({
        id: p.id,
        status: selectedStatus.value,
      })),
    };
  };

  const act_7315092382398562_closeModal_7bg4buya8 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_7bg4buya8_getActionArgs,
    });
  const inst_evh_7315092382398562_close_7c9078f1s =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_7bg4buya8,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_7c9078f1s],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_wa68r3okk =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1l6ezkfrc =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_wa68r3okk,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1l6ezkfrc],
    event: 'reload',
  });
  const act_2248226175642056_reloadGrid_qdul43ta6 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2248226175642056_reloadGrid_qdul43ta6_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2248226175642056_saved_5aiz187jv =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadGrid_qdul43ta6,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_2248226175642056_reloadGrid_rnw99lg8q =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2248226175642056_reloadGrid_rnw99lg8q_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2248226175642056_close_ta9ybs1rj =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadGrid_rnw99lg8q,
      event: 'close',
      when: inst_evh_2248226175642056_close_ta9ybs1rj_getWhen,
      displayName: 'reloadGrid',
    });
  const act_2248226175642056_showModal_x4uqpowz3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_x4uqpowz3_getActionArgs,
      displayName: 'showRegisterEntryModal',
      events: [
        inst_evh_2248226175642056_saved_5aiz187jv,
        inst_evh_2248226175642056_close_ta9ybs1rj,
      ],
    });
  const inst_evh_2248226175642056_command_dybgri7gx =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_x4uqpowz3,
      event: 'command',
      when: inst_evh_2248226175642056_command_dybgri7gx_getWhen,
      displayName: 'showRegisterEntryModal',
    });
  const act_2248226175642056_reloadGrid_c9q1bjjgf =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2248226175642056_reloadGrid_c9q1bjjgf_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2248226175642056_saved_cqxum988t =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadGrid_c9q1bjjgf,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_2248226175642056_reloadGrid_soo2dfu3c =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2248226175642056_reloadGrid_soo2dfu3c_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2248226175642056_close_x122nr1n0 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadGrid_soo2dfu3c,
      event: 'close',
      when: inst_evh_2248226175642056_close_x122nr1n0_getWhen,
      displayName: 'reloadGrid',
    });
  const act_2248226175642056_showModal_819f2nr4y =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_819f2nr4y_getActionArgs,
      displayName: 'showRefEntryModal',
      events: [
        inst_evh_2248226175642056_saved_cqxum988t,
        inst_evh_2248226175642056_close_x122nr1n0,
      ],
    });
  const inst_evh_2248226175642056_command_qzhk6nqph =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_819f2nr4y,
      event: 'command',
      when: inst_evh_2248226175642056_command_qzhk6nqph_getWhen,
      displayName: 'showRefEntryModal',
    });
  const act_2248226175642056_script_yzss1ku3r = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_yzss1ku3r_getActionArgs,
  });
  const inst_evh_2248226175642056_command_z0i6334cq =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_yzss1ku3r,
      event: 'command',
      when: inst_evh_2248226175642056_command_z0i6334cq_getWhen,
      displayName: 'script',
    });
  const act_2248226175642056_reloadGrid_op5ajbcdf =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2248226175642056_reloadGrid_op5ajbcdf_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2248226175642056_saved_hgjg35ktp =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadGrid_op5ajbcdf,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_2248226175642056_reloadGrid_cqgnt76uk =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2248226175642056_reloadGrid_cqgnt76uk_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2248226175642056_close_naukgmo34 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadGrid_cqgnt76uk,
      event: 'close',
      when: inst_evh_2248226175642056_close_naukgmo34_getWhen,
      displayName: 'reloadGrid',
    });
  const act_2248226175642056_showModal_wxkxmt1re =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_wxkxmt1re_getActionArgs,
      displayName: 'showInvoiceModal',
      events: [
        inst_evh_2248226175642056_saved_hgjg35ktp,
        inst_evh_2248226175642056_close_naukgmo34,
      ],
    });
  const inst_evh_2248226175642056_command_8yewxjl6g =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_wxkxmt1re,
      event: 'command',
      when: inst_evh_2248226175642056_command_8yewxjl6g_getWhen,
      displayName: 'showInvoiceModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_dybgri7gx,
      inst_evh_2248226175642056_command_qzhk6nqph,
      inst_evh_2248226175642056_command_z0i6334cq,
      inst_evh_2248226175642056_command_8yewxjl6g,
    ],
    event: 'command',
  });
  const act_2248226175642056_setData_11wtww4p1 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_11wtww4p1_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_5dwt7rkoe =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_11wtww4p1,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_5dwt7rkoe],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_setData_kvol6xsr9 = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_kvol6xsr9_getActionArgs,
  });
  const inst_evh_4615685607825208_updateModelValue_qnhzumggb =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_kvol6xsr9,
      event: 'update:modelValue',
      displayName: 'setData',
    });
  const act_4615685607825208_reloadGrid_1ii9ar4yh =
    new actionTypes.ReloadGridAction({
      actionArgs: act_4615685607825208_reloadGrid_1ii9ar4yh_getActionArgs,
    });
  const inst_evh_4615685607825208_updateModelValue_i2hcrdnkn =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadGrid_1ii9ar4yh,
      event: 'update:modelValue',
      displayName: 'reloadGrid',
    });
  const inst_evh_4615685607825208_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_4615685607825208_updateModelValue_qnhzumggb,
        inst_evh_4615685607825208_updateModelValue_i2hcrdnkn,
      ],
      event: 'update:modelValue',
    });
  const act_2095089031469157_reloadGrid_xwnt9qv7e =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2095089031469157_reloadGrid_xwnt9qv7e_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2095089031469157_saved_tygxbo9ne =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadGrid_xwnt9qv7e,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_2095089031469157_reloadGrid_jqy5gz3h5 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2095089031469157_reloadGrid_jqy5gz3h5_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2095089031469157_close_7eh24imf7 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadGrid_jqy5gz3h5,
      event: 'close',
      when: inst_evh_2095089031469157_close_7eh24imf7_getWhen,
      displayName: 'reloadGrid',
    });
  const act_2095089031469157_showModal_v6myx8adi =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_v6myx8adi_getActionArgs,
      events: [
        inst_evh_2095089031469157_saved_tygxbo9ne,
        inst_evh_2095089031469157_close_7eh24imf7,
      ],
    });
  const inst_evh_2095089031469157_click_fvf3nlt4k =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_v6myx8adi,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_fvf3nlt4k],
    event: 'click',
  });
  const act_7439928539205587_setData_qjyqhmw51 = new actionTypes.SetDataAction({
    actionArgs: act_7439928539205587_setData_qjyqhmw51_getActionArgs,
  });
  const inst_evh_7439928539205587_clickPreventStop_kusqhx7x5 =
    new actionTypes.EventHandlerImpl({
      action: act_7439928539205587_setData_qjyqhmw51,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_7439928539205587_reloadGrid_olydkpb4o =
    new actionTypes.ReloadGridAction({
      actionArgs: act_7439928539205587_reloadGrid_olydkpb4o_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_7439928539205587_success_czjeoiob6 =
    new actionTypes.EventHandlerImpl({
      action: act_7439928539205587_reloadGrid_olydkpb4o,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_7439928539205587_request_at5ivkzvh = new actionTypes.RequestAction({
    actionArgs: act_7439928539205587_request_at5ivkzvh_getActionArgs,
    displayName: 'setStatus',
    events: [inst_evh_7439928539205587_success_czjeoiob6],
  });
  const inst_evh_7439928539205587_clickPreventStop_9oel9a6dr =
    new actionTypes.EventHandlerImpl({
      action: act_7439928539205587_request_at5ivkzvh,
      event: 'click.prevent.stop',
      displayName: 'setStatus',
    });
  const inst_evh_7439928539205587_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_7439928539205587_clickPreventStop_kusqhx7x5,
        inst_evh_7439928539205587_clickPreventStop_9oel9a6dr,
      ],
      event: 'click.prevent.stop',
    });
  const act_2155315188409751_setData_yux6pg2ng = new actionTypes.SetDataAction({
    actionArgs: act_2155315188409751_setData_yux6pg2ng_getActionArgs,
  });
  const inst_evh_2155315188409751_clickPreventStop_lpl8cr0on =
    new actionTypes.EventHandlerImpl({
      action: act_2155315188409751_setData_yux6pg2ng,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_2155315188409751_reloadGrid_5r0d5rhui =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2155315188409751_reloadGrid_5r0d5rhui_getActionArgs,
      displayName: 'reloadGrid',
    });
  const inst_evh_2155315188409751_success_lfmx5nubw =
    new actionTypes.EventHandlerImpl({
      action: act_2155315188409751_reloadGrid_5r0d5rhui,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_2155315188409751_request_egzkzvqij = new actionTypes.RequestAction({
    actionArgs: act_2155315188409751_request_egzkzvqij_getActionArgs,
    displayName: 'setStatus',
    events: [inst_evh_2155315188409751_success_lfmx5nubw],
  });
  const inst_evh_2155315188409751_clickPreventStop_tymh77al0 =
    new actionTypes.EventHandlerImpl({
      action: act_2155315188409751_request_egzkzvqij,
      event: 'click.prevent.stop',
      displayName: 'setStatus',
    });
  const inst_evh_2155315188409751_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_2155315188409751_clickPreventStop_lpl8cr0on,
        inst_evh_2155315188409751_clickPreventStop_tymh77al0,
      ],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_7c9078f1s,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_1l6ezkfrc,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_saved_5aiz187jv,
    reloadGrid: inst_evh_2248226175642056_saved_5aiz187jv,
    inst_evh_2248226175642056_close_ta9ybs1rj,
    inst_evh_2248226175642056_command_dybgri7gx,
    showRegisterEntryModal: inst_evh_2248226175642056_command_dybgri7gx,
    inst_evh_2248226175642056_saved_cqxum988t,
    inst_evh_2248226175642056_close_x122nr1n0,
    inst_evh_2248226175642056_command_qzhk6nqph,
    showRefEntryModal: inst_evh_2248226175642056_command_qzhk6nqph,
    inst_evh_2248226175642056_command_z0i6334cq,
    inst_evh_2248226175642056_saved_hgjg35ktp,
    inst_evh_2248226175642056_close_naukgmo34,
    inst_evh_2248226175642056_command_8yewxjl6g,
    showInvoiceModal: inst_evh_2248226175642056_command_8yewxjl6g,
    inst_evh_2248226175642056_command,
    inst_evh_2248226175642056_selectedRowsChanged_5dwt7rkoe,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_updateModelValue_qnhzumggb,
    inst_evh_4615685607825208_updateModelValue_i2hcrdnkn,
    inst_evh_4615685607825208_updateModelValue,
    inst_evh_2095089031469157_saved_tygxbo9ne,
    inst_evh_2095089031469157_close_7eh24imf7,
    inst_evh_2095089031469157_click_fvf3nlt4k,
    inst_evh_2095089031469157_click,
    inst_evh_7439928539205587_clickPreventStop_kusqhx7x5,
    inst_evh_7439928539205587_success_czjeoiob6,
    inst_evh_7439928539205587_clickPreventStop_9oel9a6dr,
    setStatus: inst_evh_7439928539205587_clickPreventStop_9oel9a6dr,
    inst_evh_7439928539205587_clickPreventStop,
    inst_evh_2155315188409751_clickPreventStop_lpl8cr0on,
    inst_evh_2155315188409751_success_lfmx5nubw,
    inst_evh_2155315188409751_clickPreventStop_tymh77al0,
    inst_evh_2155315188409751_clickPreventStop,
  };

  const evh_7315092382398562_close_7c9078f1s = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_7c9078f1s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_1l6ezkfrc = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1l6ezkfrc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_5aiz187jv = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_5aiz187jv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_ta9ybs1rj = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_ta9ybs1rj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_dybgri7gx = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_dybgri7gx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_cqxum988t = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_cqxum988t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_x122nr1n0 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_x122nr1n0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_qzhk6nqph = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_qzhk6nqph.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_z0i6334cq = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_z0i6334cq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_hgjg35ktp = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_hgjg35ktp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_naukgmo34 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_naukgmo34.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_8yewxjl6g = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_8yewxjl6g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_5dwt7rkoe = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_5dwt7rkoe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue_qnhzumggb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_updateModelValue_qnhzumggb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue_i2hcrdnkn = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_updateModelValue_i2hcrdnkn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_saved_tygxbo9ne = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_saved_tygxbo9ne.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_7eh24imf7 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_7eh24imf7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_fvf3nlt4k = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_fvf3nlt4k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_clickPreventStop_kusqhx7x5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7439928539205587_clickPreventStop_kusqhx7x5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_success_czjeoiob6 = async (event, scope) => {
    return await actions.inst_evh_7439928539205587_success_czjeoiob6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_clickPreventStop_9oel9a6dr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7439928539205587_clickPreventStop_9oel9a6dr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7439928539205587_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2155315188409751_clickPreventStop_lpl8cr0on = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2155315188409751_clickPreventStop_lpl8cr0on.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2155315188409751_success_lfmx5nubw = async (event, scope) => {
    return await actions.inst_evh_2155315188409751_success_lfmx5nubw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2155315188409751_clickPreventStop_tymh77al0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2155315188409751_clickPreventStop_tymh77al0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2155315188409751_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2155315188409751_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RegisterView"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="register"
      :headerTitle="pgettext('Register', 'Registers')"
      :items="selectedRegisterId && selectedVersionId ? ds : []"
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'register-entries')"
      footer-totals
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      v-if="loaded"
      @command="evh_2248226175642056_command($event, {})"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <UcRegisterSelector
            :modelValue="states"
            cacheKey="registerView"
            @update:modelValue="
              evh_4615685607825208_updateModelValue($event, {})
            "
          >
          </UcRegisterSelector>
          <ItpButton
            :icon="$config.register.createEntry"
            :text="pgettext('Register', 'Add entry...')"
            size="md"
            variant="light"
            v-if="isRegisterSelected"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <BDropdown
            size="md"
            variant="light"
            toggle-class="itp-button border"
            :tooltip="pgettext('Register', 'Set status to selected items')"
            strategy="fixed"
            :disabled="
              !selectedItems ||
              !selectedItems.length ||
              (actions.setStatus && actions.setStatus.isRunning)
            "
            v-if="statuses && statuses.length"
          >
            <template #button-content>
              <span>
                <BSpinner
                  small
                  type="grow"
                  class="me-1"
                  v-if="actions.setStatus.isRunning"
                >
                </BSpinner>
                <ItpText
                  :text="pgettext('Register', 'Set status')"
                  class="mx-1"
                >
                </ItpText>
              </span>
            </template>
            <BDropdownItem
              :disabled="!selectedItems || !selectedItems.length"
              :key="status"
              v-for="status in statuses"
              @click.prevent.stop="
                evh_7439928539205587_clickPreventStop($event, { status })
              "
            >
              <ItpText :text="status"> </ItpText>
            </BDropdownItem>
            <hr />
            <BDropdownItem
              :disabled="!selectedItems || !selectedItems.length"
              @click.prevent.stop="
                evh_2155315188409751_clickPreventStop($event, {})
              "
            >
              <ItpText :text="pgettext('Register', 'Clear status')"> </ItpText>
            </BDropdownItem>
          </BDropdown>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
